import React from "react";
import { useInView, IntersectionOptions } from "react-intersection-observer";
import { motion, MotionStyle, Variants } from "framer-motion";

type Props = {
  children: JSX.Element | JSX.Element[];

  /**
   * Delay before animaton kicks in. Default: 0.25
   */
  delay?: number;

  /**
   * Time in seconds for animation to complete. Default 0.5
   */
  duration?: number;

  /**
   * How many pixels from the bottom should it move from? Default: 25
   */
  yOffset?: number;

  /**
   * How many pixels from the left should it move from? Default: 0
   */
  xOffset?: number;

  /**
   * Only trigger animation once? Default: true
   */
  triggerOnce?: boolean;

  /**
   * ByPass any fade animation, just render the children. Default: false
   */
  disabled?: boolean;

  /**
   * Dont fade in automatically when the children is inside the viewport. Use this prop to control it manually.
   */
  overrideInView?: boolean;

  /**
   * Override the intersection options provided to useInView
   */
  inViewOptions?: IntersectionOptions;

  style?: MotionStyle;
};

export default function FadeInUp({
  children,
  delay,
  duration,
  xOffset,
  yOffset,
  triggerOnce,
  disabled,
  style,
  overrideInView,
  inViewOptions
}: Props) {
  if (disabled || process.env.DISABLE_ANIMATION) {
    return <>{children}</>; // Useful to disable the animation
  }

  const defaults = {
    duration: 0.5,
    delay: 0.25,
    xOffset: 0,
    yOffset: 25,
    easing: [0.42, 0, 0.58, 1]
  };

  // Fade in after element enters the viewport
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: triggerOnce !== undefined ? triggerOnce : true,
    ...(inViewOptions || {})
  });

  const variants: Variants = {
    initial: {
      opacity: 0,
      x: xOffset !== undefined ? xOffset : defaults.xOffset,
      y: yOffset !== undefined ? yOffset : defaults.yOffset,
      transition: { when: "afterChildren" }
    },
    enter: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        when: "beforeChildren",
        duration: duration !== undefined ? duration : defaults.duration,
        delay: delay !== undefined ? delay : defaults.delay,
        easing: defaults.easing
      }
    }
  };

  const resolvedInView = overrideInView !== undefined ? overrideInView : inView;

  return (
    <motion.div
      ref={ref}
      variants={variants}
      animate={resolvedInView ? "enter" : "initial"}
      style={style}
    >
      {children}
    </motion.div>
  );
}
